import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { Constants } from 'utilities/constants';
import { createGUID } from 'utilities';

export const getObjectDefaults = type => {
  switch (type) {
    case Constants.ViewsObjectType.PROPERTYDATA:
      return {
        groupLevel: 'auto',
        pinDisplayType: null,
        pinColorIds: {
          halloween_satellite: Constants.ViewsPropertyDataPinColor.GREEN,
          hybrid: Constants.ViewsPropertyDataPinColor.GREEN,
          satellite: Constants.ViewsPropertyDataPinColor.GREEN,
          roadmap: Constants.ViewsPropertyDataPinColor.GREEN,
          default: Constants.ViewsPropertyDataPinColor.GREEN,
          terrain: Constants.ViewsPropertyDataPinColor.GREEN,
        },
        pinColorPaletteId: 'shapeDefault',
        filter: {
          filterValue: null,
          filterExpression: null
        },
        filterGeo: null
      }
    case Constants.ViewsObjectType.MARKETS:
      return {
        geoLevel: 'auto',
        shapeLabel: 'MedianIncome',
        customPalette: {
          id: 'yellowToBlue',
          c0: "#ecec37",
          c1: "#90e0dd",
          c2: "#5986d6",
          opacity: 'dd'
        },
        fillOpacity: 0.7,
        strokeWeight: 1
      }
    case Constants.ViewsObjectType.LINE:
      return {
        size: 1,
        color: '#5eb995',
        data: null,
        isTitleVisible: false,
        needsPositionSet: true
      }
    case Constants.ViewsObjectType.SHAPE:
      return {
        size: 1,
        color: '#5eb5b9',
        fillOpacity: 0.3,
        data: null,
        isTitleVisible: false,
        needsPositionSet: true
      }
    case Constants.ViewsObjectType.CIRCLE:
      return {
        size: 1,
        color: '#5eb5b9',
        fillOpacity: 0.3,
        data: null,
        isTitleVisible: false,
        isMeasuringVisible: true,
        needsPositionSet: true
      }
    case Constants.ViewsObjectType.PIN:
      return {
        size: 1,
        color: '#49afff',
        position: null,
        isTitleVisible: false,
        needsPositionSet: true
      }
    case Constants.ViewsObjectType.DISTANCEMEASURING:
      return {
        size: 1,
        color: {
          halloween_satellite: '#b432ff',
          hybrid: '#b432ff',
          satellite: '#b432ff',
          roadmap: '#4286f4',
          default: '#4286f4',
          terrain: '#4286f4',
        },
        data: null,
        borderColor: {
          halloween_satellite: '#b432ff',
          hybrid: '#b432ff',
          satellite: '#b432ff',
          roadmap: '#000000',
          default: '#000000',
          terrain: '#000000',
        },
        isTitleVisible: false,
        isMeasuringVisible: true,
        needsPositionSet: true
      }
    case Constants.ViewsObjectType.LABEL:
      return {
        size: 12,
        autoSizeValue: 12,
        data: null,
        fontColor: '#fff',
        labelBackgroundColor: '#5eb5b9',
        fontWeight: 700,
        isFontDependsFromZoom: false,
        needsPositionSet: true
      }
    default:
      return { test: 'test'}
  }
}

export const getObjectDataProperty = (type) => {
  switch (type) {
    case Constants.ViewsObjectType.PIN:
      return 'position';
    case Constants.ViewsObjectType.LINE:
    case Constants.ViewsObjectType.SHAPE:
    case Constants.ViewsObjectType.CIRCLE:
    case Constants.ViewsObjectType.DISTANCEMEASURING:
    case Constants.ViewsObjectType.LABEL:
      return 'data';
    default:
      return null;
  }
}

export const viewObjects = {
  [Constants.ViewsObjectType.PROPERTYDATA]: {
    displayType: 'Property Data',
    icon: 'M17,16H15V22H12V17H8V22H5V16H3L10,10L17,16M6,2L10,6H9V9H7V6H5V9H3V6H2L6,2M18,3L23,8H22V12H19V9H17V12H15.34L14,10.87V8H13L18,3Z',
    drawingMode: 'polygon'
  },
  [Constants.ViewsObjectType.MARKETS]: {
    displayType: 'Geographic Data',
    icon: 'M12,3L2,12H5V20H19V12H22M9,18H7V12H9M13,18H11V10H13M17,18H15V14H17'
  },
  [Constants.ViewsObjectType.PIN]: {
    displayType: 'Pin',
    icon: 'M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z',
    drawingMode: 'marker'
  },
  [Constants.ViewsObjectType.LINE]: {
    displayType: 'Line',
    icon: 'M15,3V7.59L7.59,15H3V21H9V16.42L16.42,9H21V3M17,5H19V7H17M5,17H7V19H5',
    drawingMode: 'polyline'
  },
  [Constants.ViewsObjectType.SHAPE]: {
    displayType: 'Shape',
    icon: 'M2,2V8H4.28L5.57,16H4V22H10V20.06L15,20.05V22H21V16H19.17L20,9H22V3H16V6.53L14.8,8H9.59L8,5.82V2M4,4H6V6H4M18,5H20V7H18M6.31,8H7.11L9,10.59V14H15V10.91L16.57,9H18L17.16,16H15V18.06H10V16H7.6M11,10H13V12H11M6,18H8V20H6M17,18H19V20H17',
    drawingMode: 'polygon'
  },
  [Constants.ViewsObjectType.CIRCLE]: {
    displayType: 'Circle',
    icon: 'M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z',
    drawingMode: 'circle'
  },
  [Constants.ViewsObjectType.DISTANCEMEASURING]: {
    displayType: 'Distance Measuring',
    icon: 'M17.7 17.7L16.6 18.8L15.9 18L17 17L15 15L14 16.1L13.3 15.4L14.4 14.3L12.5 12.4L11.4 13.5L10.7 12.8L11.8 11.7L9.8 9.8L8.7 10.9L8 10.2L9 9L7.1 7.1L6 8.1L5.3 7.4L6.4 6.3L4 4V20H20L17.7 17.7M7 17V11.2L12.8 17H7Z',
    // disabled: true
    drawingMode: 'Distance Measuring'
  },
  [Constants.ViewsObjectType.LABEL]: {
    displayType: 'Label',
    icon: 'M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16zM16 17H5V7h11l3.55 5L16 17z',
  }
}

// The list endpoint only returns another user's Private view when it's been shared with the current user
export const isViewSharedWithUser = (view, user) => view?.privacy === 'Private' && view.createdByUserId != null && view.createdByUserId !== user?.id;

export const viewMatchesFilter = (view, filterType, user) => {
  switch (filterType) {
    case 'MY_VIEWS':
      return view.createdByUserId === user.id;
    case 'SHARED_WITH_ME':
      return isViewSharedWithUser(view, user);
    default:
      return true;
  }
}

export const initialState = {
  views: null,
  viewsLoadingState: { isLoading: false, message: null },
  selectedViewId: null,
  selectedView: null,
  selectedViewIsOpen: false,
  selectedViewLoadingState: { isLoading: false, message: null },
  selectedViewIsDirty: false,
  focusedObjectId: null,
  highlightedObjectId: null,
  selectedObjectId: null,
  lastCreatedObjectId: null,
  lastCreatedLayerId: null,
  saveViewDialogParameters: { dialogIsOpen: false, navigateToViewId: null, navigateToView: null },
  activeDrawingObject: null,
  activeEditingObject: null,
  radius: 0,
  manualRadius: 0,
  distanceMeasuringTotalArea: '0 mi',
  distanceMeasuringTotalDistance: '0 mi²',
  sortObject: [
    { name: 'Alphabetical', type: 'ALPHABETICAL', order: null, },
    { name: 'Date Created', type: 'DATE_CREATED', order: null, },
    { name: 'Last Updated', type: 'LAST_UPDATED', order: 'desc', },
    { name: 'My Views', type: 'MY_VIEWS', filter: false, },
    { name: 'Shared With Me', type: 'SHARED_WITH_ME', filter: false, },
    { name: 'Clear', type: 'DEFAULT' },
  ],
  isSideBarOpen: false,
}

// create slice will call createAction and createReducer for us internally
const slice = createSlice({
  name: 'exploreViewsSlice',
  initialState,
  reducers: {
    setExploreViews: (state, action) => {
      state.views = action.payload;
    },
    setExploreViewsLoadingState: (state, action) => {
      state.viewsLoadingState = action.payload;
    },
    setExploreSelectedViewId: {
      reducer: (state, action) => {
        state.selectedViewId = action.payload.selectedViewId;
      },
      prepare: (selectedViewId, shouldFetch = true, shouldClearDataObjects = true, callback) => ({ payload: { selectedViewId, shouldFetch, shouldClearDataObjects, callback } })
    },
    setExploreSelectedViewIsOpen: (state, action) => {
      state.selectedViewIsOpen = action.payload;
    },
    setExploreSelectedViewLoadingState: (state, action) => {
      state.selectedViewLoadingState = action.payload;
    },
    setExploreSelectedViewIsDirty: (state, action) => {
      state.selectedViewIsDirty = action.payload;
    },
    setExploreSelectedView: (state, action) => {
      state.selectedView = action.payload;
    },
    setExploreFocusedObjectId: (state, action) => {
      state.focusedObjectId = action.payload;
    },
    setExploreHighlightedObjectId: (state, action) => {
      state.highlightedObjectId = action.payload;
    },
    setExploreSelectedObjectId: (state, action) => {
      state.selectedObjectId = action.payload;
    },
    setExploreLastCreatedObjectId: (state, action) => {
      state.lastCreatedObjectId = action.payload;
    },
    setExploreLastCreatedLayerId: (state, action) => {
      state.lastCreatedLayerId = action.payload;
    },
    setExploreSaveViewDialogParameters: {
      reducer: (state, action) => {
        state.saveViewDialogParameters = action.payload;
      },
      prepare: (dialogIsOpen, navigateToViewId, navigateToView) => ({ payload: { dialogIsOpen, navigateToViewId, navigateToView }})
    },
    setExploreActiveDrawingObject: (state, action) => {
      state.activeDrawingObject = action.payload
    },
    setExploreActiveEditingObject: (state, action) => {
      state.activeEditingObject = action.payload
    },
    setRadius: (state, action) => {
      state.radius = action.payload
    },
    setManualRadius: (state, action) => {
      state.manualRadius = action.payload
    },
    setDistanceMeasuringTotalArea: (state, action) => {
      state.distanceMeasuringTotalArea = action.payload
    },
    setDistanceMeasuringTotalDistance: (state, action) => {
      state.distanceMeasuringTotalDistance = action.payload
    },
    setIsSideBarOpen: (state, action) => {
      state.isSideBarOpen = action.payload;
    },
    updateSelectedExploreView: {
      reducer: (state, action) => {
        state.selectedView = {
          ...state.selectedView,
          ...action.payload.update
        }
      },
      prepare: (update, shouldNotMarkDirty) => ({ payload: { update, shouldNotMarkDirty }})
    },
    createExploreViewObject: {
      reducer: (state, action) => {
        const { objectId, layerId, objectType, data } = action.payload;
        const { layers, objects } = state.selectedView;
        const layer = layers.find(layer => layer.id === layerId);
        if (layer) {
          state.lastCreatedObjectId = objectId;
          const newObject = {
            id: objectId,
            type: objectType,
            index: layer.objectIds.length,
            visible: true,
            description: viewObjects[objectType]?.displayType ?? objectType,
            layerId,
            ...getObjectDefaults(objectType),
            ...data
          }
          objects.push(newObject);
          layer.objectIds.push(objectId);
        }
      },
      prepare: (objectId, layerId, objectType, data) => ({ payload: { objectId, layerId, objectType, data } })
    },
    updateExploreObjectById: {
      reducer: (state, action) => {
        const { objectId, update } = action.payload;
        const newSelectedView = {
          ...state.selectedView,
          objects: state.selectedView.objects.map(object => {
            if (object.id === objectId) {
              let needsPositionSet = object.needsPositionSet;
              const dataProperty = getObjectDataProperty(object.type);
              if (dataProperty && update.hasOwnProperty(dataProperty)) {
                needsPositionSet = update[dataProperty] == null;
              }
              return {
                ...object,
                ...update,
                needsPositionSet
              }
            }
            return object;
          })
        }
        state.selectedView = newSelectedView;
      },
      prepare: (objectId, update, shouldNotMarkDirty) => ({ payload: { objectId, update, shouldNotMarkDirty } })
    },
    createExploreViewLayer: {
      reducer: (state, action) => {
        const index = state.selectedView.layers.length
        const newLayer = {
          id: 'tempLayer-' + createGUID(),
          name: action.payload?.name ?? 'New Layer',
          index,
          visible: true,
          objectIds: []
        }
        state.lastCreatedLayerId = newLayer.id;
        state.selectedView.layers.push(newLayer);
      },
      prepare: (name, idToDuplicate) => ({ payload: { name, idToDuplicate } })
    },
    deleteExploreViewLayer: (state, action) => {
      const newLayers = state.selectedView.layers.filter(layer => layer.id !== action.payload).sort((a, b) => a.index - b.index).map((layer, index) => {
        return {
          ...layer,
          index
        }
      });
      state.selectedView.layers = newLayers;
    },
    setExploreViewSharedWithUsers: { // Keeps the list and the open view in step after a sharing change, without refetching
      reducer: (state, action) => {
        const { viewId, sharedWithUsers } = action.payload;
        const view = state.views?.find(view => view.id === viewId);
        if (view) view.sharedWithUsers = sharedWithUsers;
        if (state.selectedView?.id === viewId) state.selectedView.sharedWithUsers = sharedWithUsers;
      },
      prepare: (viewId, sharedWithUsers) => ({ payload: { viewId, sharedWithUsers } })
    },
    sortExploreViews: (state, action) => {
      state.sortObject = state.sortObject.map(obj => {
        const newObject = { ...obj };
        const isOrder = newObject.hasOwnProperty('order');
        const isClearFilter =
          action.payload === 'DEFAULT'
          && newObject.hasOwnProperty('filter');

        if (action.payload === obj.type && action.payload !== 'DEFAULT') {
          switch (newObject.order) {
            case null:
              newObject.order = 'asc';
              break;
            case 'asc':
              newObject.order = 'desc';
              break;
            case 'desc':
              newObject.order = null;
              break;
            default:
          }
        } else {
          if (isOrder) newObject.order = null;
          if (isClearFilter) newObject.filter = false;
        }

        return newObject;
      });
    },
    filterExploreViews: (state, action) => {
      state.sortObject = state.sortObject.map(obj => {
        const newObject = { ...obj };

        if (action.payload === obj.type && obj.hasOwnProperty('filter')) {
          newObject.filter = !newObject.filter;
        } else if (obj.hasOwnProperty('filter')) { // Filters are mutually exclusive: a view can't be both mine and shared with me
          newObject.filter = false;
        }

        return newObject;
      });
    },

    // Redux saga actions, no reducers
    deleteExploreViewObject: { // Triggers exploreViewsSaga
      reducer: () => { },
      prepare: (layerId, objectId) => ({ payload: { layerId, objectId } })
    },
    fetchExploreViews: () => { }, // Triggers exploreViewsSaga
    createExploreView: () => { }, // Triggers exploreViewsSaga
    updateExploreViewsOrder: () => {}, // Triggers exploreViewsSaga
    createExploreSystemView: { // Triggers exploreViewsSaga
      reducer: () => { },
      prepare: (name, filter) => ({ payload: { name, filter }})
    },
    saveExploreView: () => { }, // Triggers exploreViewsSaga
    deleteExploreView: () => { }, // Triggers exploreViewsSaga
    updateExploreViewSharing: { // Triggers exploreViewsSaga
      reducer: () => { },
      prepare: (viewId, userIds, callback) => ({ payload: { viewId, userIds, callback } })
    },
    requestExploreSelectedViewIdChange: { // Triggers exploreViewsSaga
      reducer: () => { },
      prepare: (id, callback) => ({ payload: { id, callback }})
    },
    highlightExploreViewObject: { // Triggers exploreViewsSaga
      reducer: () => { },
      prepare: (id, objectType) => ({ payload: { id, objectType }})
    },
    handleExploreViewObjectVisibilityChange: { // Triggers exploreViewsSaga
      reducer: () => { },
      prepare: (objectIds, visibility, toggleOthers) => ({ payload: { objectIds, visibility, toggleOthers }})
    }
  }
});

export default slice.reducer;

export const exploreViewsActions = slice.actions;

// Memoized selectors
export const exploreViewsSelectors = {
  exploreViewsStateSelector: createSelector(
    state => state.exploreViews,
    views => views
  ),
  exploreViewsSelector: createSelector(
    state => state.exploreViews.views,
    views => views
  ),
  exploreSelectedViewSelector: createSelector(
    state => state.exploreViews.selectedView,
    selectedView => selectedView
  ),

}
